// Suivi de caisse (livre de caisse) : alimentations (entrées / débit) et dépenses (sorties / crédit),
// présentées par mois avec solde reporté, solde courant ligne par ligne et solde final. Registre indépendant des charges.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  AddOutlined, ChevronLeft, ChevronRight, DeleteOutline, EditOutlined, FileDownloadOutlined, PrintOutlined,
  SearchOutlined, VisibilityOutlined,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { caisseAPI } from '../../services/api';
import { apiError, today } from '../../utils/format';
import { ecrireExcel } from '../../utils/excel';
import { tokens } from '../../theme';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const libMois = (m) => `${MOIS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const moisDe = (d) => d.slice(0, 7);
const decale = (m, n) => { const d = new Date(`${m}-01T00:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 7); };
const F = (n) => (n === null || n === undefined ? '—' : `${Math.round(n).toLocaleString('fr-FR')} F`);
const frDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const num = (v) => Number(String(v ?? '').replace(',', '.'));
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const fmt = (n) => (n === null || n === undefined || n === 0 ? '' : `${Math.round(n).toLocaleString('fr-FR')} F`);

// Ouvre une fenêtre d'impression avec le livre de caisse du mois (aperçu avant impression / PDF).
function imprimer(data, mois) {
  if (!data) return;
  const lignes = data.mouvements.map((m) => `<tr>
    <td>${frDate(m.date_mouvement)}</td><td>${esc(m.libelle)}</td>
    <td class="r">${m.quantite ?? ''}</td><td class="r">${m.prix_unitaire ? fmt(m.prix_unitaire) : ''}</td>
    <td class="r pos">${fmt(m.alimentation)}</td><td class="r neg">${fmt(m.depense)}</td>
    <td class="r b">${fmt(m.solde)}</td><td>${esc(m.observation || '')}</td></tr>`).join('');
  const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Suivi de caisse — ${esc(libMois(mois))}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;margin:24px;}
      h1{font-size:18px;margin:0 0 2px;} .sub{color:#666;font-size:13px;margin-bottom:16px;}
      table{width:100%;border-collapse:collapse;font-size:12px;}
      th,td{border:1px solid #ccc;padding:5px 7px;text-align:left;vertical-align:top;}
      th{background:#f2f4f7;font-size:11px;text-transform:uppercase;letter-spacing:.3px;}
      .r{text-align:right;white-space:nowrap;} .b{font-weight:700;} .pos{color:#1b7f3b;} .neg{color:#c0392b;}
      .rep td{font-style:italic;color:#555;} .tot td{border-top:2px solid #333;font-weight:700;}
      .fin td{font-weight:700;background:#f2f4f7;}
      @media print{body{margin:0;}}
    </style></head><body>
    <h1>DOCC — Suivi de caisse</h1>
    <div class="sub">${esc(libMois(mois))} — édité le ${frDate(today())}</div>
    <table><thead><tr>
      <th>Date</th><th>Libellé</th><th class="r">Qté</th><th class="r">P.U.</th>
      <th class="r">Alimentation</th><th class="r">Dépense</th><th class="r">Solde</th><th>Commentaire</th>
    </tr></thead><tbody>
      <tr class="rep"><td colspan="6">Solde reporté (début ${esc(libMois(mois))})</td><td class="r b">${fmt(data.solde_reporte) || '0 F'}</td><td></td></tr>
      ${lignes || '<tr><td colspan="8" style="text-align:center;color:#888;padding:14px;">Aucun mouvement</td></tr>'}
      <tr class="tot"><td colspan="4">TOTAUX — ${esc(libMois(mois))}</td><td class="r pos">${fmt(data.total_alimentation)}</td><td class="r neg">${fmt(data.total_depense)}</td><td></td><td></td></tr>
      <tr class="fin"><td colspan="6">SOLDE FINAL — ${esc(libMois(mois))}</td><td class="r">${fmt(data.solde_final) || '0 F'}</td><td></td></tr>
    </tbody></table></body></html>`;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => w.print(), 300);
}

function ApercuDialog({ open, data, mois, onClose }) {
  if (!open || !data) return null;
  const L = ({ children, ...p }) => <TableCell sx={{ fontSize: 12.5, py: 0.6 }} {...p}>{children}</TableCell>;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <span>Aperçu — Suivi de caisse · {libMois(mois)}</span>
        <Button startIcon={<PrintOutlined />} variant="outlined" size="small" onClick={() => imprimer(data, mois)}>Imprimer / PDF</Button>
      </DialogTitle>
      <DialogContent dividers>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell><TableCell>Libellé</TableCell>
              <TableCell align="right">Alimentation</TableCell><TableCell align="right">Dépense</TableCell>
              <TableCell align="right">Solde</TableCell><TableCell>Commentaire</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ '& td': { fontStyle: 'italic', color: 'text.secondary' } }}>
              <L colSpan={4}>Solde reporté (début {libMois(mois)})</L>
              <L align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{F(data.solde_reporte)}</L><L />
            </TableRow>
            {data.mouvements.map((m) => (
              <TableRow key={m.id}>
                <L sx={{ whiteSpace: 'nowrap' }}>{frDate(m.date_mouvement)}</L>
                <L>{m.libelle}</L>
                <L align="right" sx={{ fontFamily: tokens.mono, color: 'success.main' }}>{m.alimentation ? F(m.alimentation) : ''}</L>
                <L align="right" sx={{ fontFamily: tokens.mono, color: 'error.main' }}>{m.depense ? F(m.depense) : ''}</L>
                <L align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{F(m.solde)}</L>
                <L sx={{ color: 'text.secondary' }}>{m.observation || ''}</L>
              </TableRow>
            ))}
            {!data.mouvements.length && <TableRow><L colSpan={6} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>Aucun mouvement pour {libMois(mois)}.</L></TableRow>}
            <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 } }}>
              <L colSpan={2}>TOTAUX</L>
              <L align="right" sx={{ fontFamily: tokens.mono, color: 'success.main' }}>{F(data.total_alimentation)}</L>
              <L align="right" sx={{ fontFamily: tokens.mono, color: 'error.main' }}>{F(data.total_depense)}</L>
              <L /><L />
            </TableRow>
            <TableRow sx={{ '& td': { fontWeight: 700 } }}>
              <L colSpan={4}>SOLDE FINAL</L>
              <L align="right" sx={{ fontFamily: tokens.mono }}>{F(data.solde_final)}</L><L />
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}><Button onClick={onClose}>Fermer</Button></DialogActions>
    </Dialog>
  );
}

function MouvementDialog({ open, mouvement, moisDefaut, onClose, onDone }) {
  const [f, setF] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setError('');
    if (mouvement) {
      setF({ ...mouvement, type: mouvement.alimentation > 0 ? 'ALIMENTATION' : 'DEPENSE', montant: (mouvement.alimentation || mouvement.depense) || '' });
    } else {
      setF({ type: 'DEPENSE', date_mouvement: (moisDefaut && `${moisDefaut}-01` > today()) ? `${moisDefaut}-01` : today(), libelle: '', quantite: '', prix_unitaire: '', montant: '', observation: '' });
    }
  }, [open, mouvement, moisDefaut]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const auto = f.montant === '' || f.montant === null || f.montant === undefined;
  const montantCalc = auto && f.quantite && f.prix_unitaire ? Math.round(num(f.quantite) * num(f.prix_unitaire)) : null;
  const save = async () => {
    setBusy(true); setError('');
    try {
      const payload = { ...f };
      if (mouvement) await caisseAPI.update(mouvement.id, payload); else await caisseAPI.create(payload);
      onDone(mouvement ? 'Mouvement modifié' : 'Mouvement enregistré');
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  if (!open) return null;
  const entree = f.type === 'ALIMENTATION';
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mouvement ? 'Modifier le mouvement' : 'Nouveau mouvement de caisse'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <ToggleButtonGroup exclusive fullWidth color={entree ? 'success' : 'error'} value={f.type || 'DEPENSE'}
          onChange={(e, v) => v && setF((x) => ({ ...x, type: v }))} sx={{ mb: 2 }}>
          <ToggleButton value="ALIMENTATION">Alimentation (entrée)</ToggleButton>
          <ToggleButton value="DEPENSE">Dépense (sortie)</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField type="date" label="Date *" value={f.date_mouvement || ''} onChange={set('date_mouvement')} InputLabelProps={{ shrink: true }} />
          <TextField label="Libellé *" value={f.libelle || ''} onChange={set('libelle')} inputProps={{ maxLength: 255 }} />
          <TextField label="Quantité" value={f.quantite ?? ''} onChange={set('quantite')} inputProps={{ inputMode: 'decimal' }} />
          <TextField label="Prix unitaire (F)" value={f.prix_unitaire ?? ''} onChange={set('prix_unitaire')} inputProps={{ inputMode: 'decimal' }} />
          <TextField label={entree ? 'Montant alimentation (F) *' : 'Montant dépense (F) *'} value={f.montant ?? ''} onChange={set('montant')}
            inputProps={{ inputMode: 'decimal' }} placeholder={montantCalc ? String(montantCalc) : ''}
            helperText={montantCalc ? `Calculé : ${F(montantCalc)} (modifiable)` : 'Sinon quantité × prix unitaire'} />
          <TextField label="Commentaire" value={f.observation || ''} onChange={set('observation')} inputProps={{ maxLength: 255 }}
            multiline minRows={1} sx={{ gridColumn: { sm: '1 / -1' } }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={busy || !f.libelle || !f.date_mouvement || (!f.montant && !montantCalc)} onClick={save}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
}

const SuiviCaisse = () => {
  const [mois, setMois] = useState(moisDe(today()));
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [resume, setResume] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [dlg, setDlg] = useState(null);
  const [apercu, setApercu] = useState(false);

  const charger = useCallback(() => {
    caisseAPI.list({ mois, q: q || undefined }).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
    caisseAPI.resume().then((r) => setResume(r.data)).catch(() => {});
  }, [mois, q]);
  useEffect(() => { const t = setTimeout(charger, 200); return () => clearTimeout(t); }, [charger]);

  const moisAvecDonnees = useMemo(() => (resume?.mois || []).some((m) => m.nombre), [resume]);
  const supprimer = async (m) => {
    if (!window.confirm(`Supprimer le mouvement « ${m.libelle} » ?`)) return;
    try { await caisseAPI.supprimer(m.id); setInfo('Mouvement supprimé'); charger(); } catch (e) { setError(apiError(e)); }
  };
  const exporter = async () => {
    const lignes = [
      [{ value: `Suivi de caisse — ${libMois(mois)}`, fontWeight: 'bold' }],
      ['DATE', 'LIBELLE', 'QUANTITE', 'PRIX UNITAIRE', 'ALIMENTATION (DEBIT)', 'DEPENSE (CREDIT)', 'SOLDE', 'COMMENTAIRE'],
      ['', 'Solde reporté', '', '', '', '', Math.round(data.solde_reporte), ''],
      ...data.mouvements.map((m) => [m.date_mouvement ? new Date(`${m.date_mouvement}T00:00:00Z`) : null, m.libelle,
        m.quantite ?? '', m.prix_unitaire ? Math.round(m.prix_unitaire) : '', m.alimentation ? Math.round(m.alimentation) : '',
        m.depense ? Math.round(m.depense) : '', Math.round(m.solde), m.observation || '']),
      [], ['', 'TOTAUX', '', '', Math.round(data.total_alimentation), Math.round(data.total_depense), '', ''],
      ['', 'SOLDE FINAL', '', '', '', '', Math.round(data.solde_final), ''],
    ];
    await ecrireExcel(lignes, `suivi-caisse-${mois}.xlsx`, { largeurs: [12, 40, 10, 14, 20, 18, 16, 30] });
  };

  return (
    <Layout title="Suivi de caisse" plain
      actions={<>
        <Button startIcon={<VisibilityOutlined />} disabled={!data} onClick={() => setApercu(true)}>Aperçu</Button>
        <Button startIcon={<FileDownloadOutlined />} disabled={!data?.mouvements.length} onClick={exporter}>Excel</Button>
        <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setDlg({})}>Nouveau mouvement</Button>
      </>}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => setMois(decale(mois, -1))}><ChevronLeft /></IconButton>
        <Typography sx={{ fontWeight: 700, fontSize: 18, minWidth: 150, textAlign: 'center' }}>{libMois(mois)}</Typography>
        <IconButton onClick={() => setMois(decale(mois, 1))} disabled={mois >= moisDe(today())}><ChevronRight /></IconButton>
        <TextField size="small" placeholder="Rechercher (libellé, commentaire)" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 260, flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        <Kpi compact tint="navy" label="Solde de caisse actuel" value={F(resume?.solde_global)} valueSize={22} sub="toutes périodes" />
        <Kpi compact tint="gray" label="Solde reporté" value={F(data?.solde_reporte)} valueSize={22} sub={`début ${libMois(mois)}`} />
        <Kpi compact tint="green" label={`Alimentations — ${libMois(mois)}`} value={F(data?.total_alimentation)} valueSize={22} sub="entrées" />
        <Kpi compact tint="amber" label={`Dépenses — ${libMois(mois)}`} value={F(data?.total_depense)} valueSize={22} sub="sorties" />
      </Box>

      <Panel flush>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Libellé</TableCell>
                <TableCell align="right">Quantité</TableCell><TableCell align="right">Prix unitaire</TableCell>
                <TableCell align="right">Alimentation (débit)</TableCell><TableCell align="right">Dépense (crédit)</TableCell>
                <TableCell align="right">Solde</TableCell><TableCell>Commentaire</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ '& td': { color: 'text.secondary', fontStyle: 'italic' } }}>
                <TableCell colSpan={6}>Solde reporté (début {libMois(mois)})</TableCell>
                <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{F(data?.solde_reporte)}</TableCell><TableCell /><TableCell />
              </TableRow>
              {data?.mouvements.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{frDate(m.date_mouvement)}</TableCell>
                  <TableCell><Typography sx={{ fontSize: 14 }}>{m.libelle}</Typography></TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>{m.quantite ?? '—'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{m.prix_unitaire ? F(m.prix_unitaire) : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'success.main', whiteSpace: 'nowrap' }}>{m.alimentation ? F(m.alimentation) : ''}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'error.main', whiteSpace: 'nowrap' }}>{m.depense ? F(m.depense) : ''}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600, whiteSpace: 'nowrap' }}>{F(m.solde)}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 220 }}>{m.observation || '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Modifier"><IconButton size="small" onClick={() => setDlg({ mouvement: m })}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Supprimer"><IconButton size="small" onClick={() => supprimer(m)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {data && !data.mouvements.length && <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucun mouvement pour {libMois(mois)}.{moisAvecDonnees ? ' D’autres mois contiennent des mouvements (flèches ci-dessus).' : ' Enregistrez une alimentation ou une dépense.'}</TableCell></TableRow>}
              {data && data.mouvements.length > 0 && (
                <>
                  <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 } }}>
                    <TableCell colSpan={4}>TOTAUX — {libMois(mois)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'success.main' }}>{F(data.total_alimentation)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'error.main' }}>{F(data.total_depense)}</TableCell>
                    <TableCell /><TableCell /><TableCell />
                  </TableRow>
                  <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                    <TableCell colSpan={6}>SOLDE FINAL — {libMois(mois)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{F(data.solde_final)}</TableCell><TableCell /><TableCell />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <MouvementDialog open={!!dlg} mouvement={dlg?.mouvement} moisDefaut={mois} onClose={() => setDlg(null)} onDone={(m) => { setDlg(null); setInfo(m); charger(); }} />
      <ApercuDialog open={apercu} data={data} mois={mois} onClose={() => setApercu(false)} />
    </Layout>
  );
};

export default SuiviCaisse;
